import { ViewController } from '@estee/elc-service';
import { action, computed, observable } from 'mobx';
import { DeliveryMethods, DeliveryOptions, EditDeliveryOptions } from '~constants/constants';
import { DeliveryMethodsViewController } from '~controllers/DeliveryMethodsViewController';
import { FindInStore } from '~domain/entities/FindInStore';
import { Store } from '~domain/entities/Store';
import { StoresRepository } from '~repositories/StoresRepository';
import { TransactionRepository } from '~repositories/TransactionRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { RemoveUnavailableItemsUseCase } from '~use-cases/BopisUseCases/RemoveUnavailableItemsUseCase';
import { SetDeliveryMethodUseCase } from '~use-cases/BopisUseCases/SetDeliveryMethodUseCase';
import { EditDeliveryModalViewController } from './EditDeliveryModalViewController';

export class EditDeliveryViewController extends ViewController {
    @observable private transactionRepository: TransactionRepository;
    @observable private storesRepository: StoresRepository;
    @observable private modalCtrl: EditDeliveryModalViewController;
    @observable private deliveryMethodsCtrl: DeliveryMethodsViewController;
    @observable public editDeliveryOption: EditDeliveryOptions;
    @observable public selectedStoreWithAllItems: Store;
    @observable public isCloseLoading = false;
    private findInStore: FindInStore;
    @observable private courierUnavailableSkus: number[];

    constructor() {
        super();
        this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
        this.storesRepository = diContainer.get(serviceNames.storesRepository);
        this.findInStore = diContainer.get(serviceNames.findInStore);
        this.modalCtrl = diContainer.get(serviceNames.editDeliveryModalViewController);
        this.deliveryMethodsCtrl = diContainer.get(serviceNames.deliveryMethodsViewController);
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        this.setEditDeiliveryOption(EditDeliveryOptions.SHIP_TO_HOME);
        if (this.isStoresWithAllItems) {
            this.selectedStoreWithAllItems = this.storesWithAllItems[0];
        }
        this.isLoading = false;
    };

    @action
    public setEditDeiliveryOption = (value: EditDeliveryOptions) => {
        this.editDeliveryOption = value;
    };

    @action
    onStoreWithAllItemsChange = (_: string, value: string) => {
        const selectedStore = this.storesWithAllItems.find((store) => store.id === value);
        if (selectedStore) {
            this.selectedStoreWithAllItems = selectedStore;
        }
    };

    continueHandler = async () => {
        this.isCloseLoading = true;
        this.modalCtrl.isClosingLoading = true;
        switch (this.editDeliveryOption) {
            case EditDeliveryOptions.SHIP_TO_HOME:
                await this.shipToHome();
                break;
            case EditDeliveryOptions.PICKUP_ALL_ITEMS:
                await this.pickupAllItemsAvailable();
                break;
            case EditDeliveryOptions.DROP_UNAVAILABLE:
                await this.dropUnavailableItems();
                break;
            default:
                await this.shipToHome();
        }
        this.isCloseLoading = false;
        this.modalCtrl.isClosingLoading = false;
        this.modalCtrl.progCloseModal();
    };

    shipToHome = async () => {
        await new SetDeliveryMethodUseCase({ deliveryMethod: DeliveryMethods.STANDARD }).execute();
        this.deliveryMethodsCtrl.fetchDeliveryOption();
    };

    pickupAllItemsAvailable = async () => {
        await new SetDeliveryMethodUseCase({
            deliveryMethod: DeliveryMethods.BOPIS,
            storeId: this.selectedStoreWithAllItems.id
        }).execute();
        this.storesRepository.setSelectedStoreId(this.selectedStoreWithAllItems.id);
        this.deliveryMethodsCtrl.fetchDeliveryOption();
    };

    dropUnavailableItems = async () => {
        if (this.deliveryMethodsCtrl.activeDeliveryOption === DeliveryOptions.BOPIS) {
            const { selectedStoreId, transactionStoreId } = this.storesRepository;
            const storeId = selectedStoreId || transactionStoreId;
            if (storeId) {
                await new SetDeliveryMethodUseCase({
                    deliveryMethod: DeliveryMethods.BOPIS,
                    storeId: storeId
                }).execute();
            } else {
                const defaultStore = this.storesRepository.stores[0].id;
                await new SetDeliveryMethodUseCase({
                    deliveryMethod: DeliveryMethods.BOPIS,
                    storeId: defaultStore
                }).execute();
            }
        } else if (this.deliveryMethodsCtrl.activeDeliveryOption === DeliveryOptions.COURIER) {
            await new RemoveUnavailableItemsUseCase(this.courierUnavailableSkus).execute();
            await new SetDeliveryMethodUseCase({
                deliveryMethod: DeliveryMethods.COURIER
            }).execute();
        }
    };

    @computed
    private get isStoreWithAvailability() {
        const storeWithAvailabilty = this.storesRepository.stores?.find(
            (store) => store.productIsAvailable === true
        );

        return storeWithAvailabilty;
    }

    getUnavailableItemsSkus(): string[] {
        if (this.selectedStore) {
            const cartItems = this.selectedStore.cartItems || {};
            const unavailableItemsSkus = Object.keys(cartItems).filter(
                (itemSku) => !cartItems[itemSku].is_available
            );

            return unavailableItemsSkus;
        }
        if (!this.isStoreWithAvailability) {
            return [`${this.findInStore.skuBaseId}`];
        }

        return [];
    }

    getStoresWithAllItems() {
        const { stores } = this.storesRepository;

        return stores.filter((store) => {
            const cartItems = store.cartItems;

            return Object.keys(cartItems).every((item) => cartItems[item].is_available);
        });
    }

    getOptionLabel(store: Store) {
        if (!store) {
            return '';
        }
        const distance = store.geographical?.distance.toFixed(1);

        return `${store.name} - ${
            store.address.neighbourhood
        } (${distance} ${store.geographical.unit.toLowerCase()})`;
    }

    @computed
    get selectedStore() {
        const { selectedStoreId, stores } = this.storesRepository;
        const selectedStore = stores.find((store) => store.id === selectedStoreId);

        return selectedStore;
    }

    @computed
    get unavailableItemsBopis() {
        const skus = this.getUnavailableItemsSkus().map((sku) => `${sku}`);
        if (!skus.length) {
            return [];
        }
        const orderItems = this.transactionRepository.transaction.transaction.order.items;
        const unavailableItems = orderItems.filter((item) =>
            skus.includes(`${item['sku.SKU_BASE_ID']}`)
        );

        return unavailableItems;
    }

    @computed
    get unavailableItemsCourier() {
        const storesWithAvailableSku = this.deliveryMethodsCtrl.storesWithAvailableSku;
        const orderItems = this.transactionRepository.transaction.transaction.order.items;
        let unavailableItemsSkus: number[];
        if (!storesWithAvailableSku.length) {
            unavailableItemsSkus = [this.findInStore.skuBaseId];
        } else {
            const courierStore = storesWithAvailableSku[0];
            const cartItems = courierStore.cartItems || {};
            const skus = Object.keys(cartItems).filter(
                (itemSku) => !cartItems[itemSku].is_available
            );
            unavailableItemsSkus = skus.map(Number);
        }
        const unavailableItems = orderItems.filter((item) =>
            unavailableItemsSkus.includes(item['sku.SKU_BASE_ID'])
        );
        this.courierUnavailableSkus = unavailableItemsSkus;

        return unavailableItems;
    }

    @computed
    get unavailableItemsInfo() {
        const deliveryMethod = this.transactionRepository.getDeliveryMethod;

        return deliveryMethod === DeliveryMethods.BOPIS
            ? this.unavailableItemsBopis
            : this.unavailableItemsCourier;
    }

    @computed
    get isStoresWithAllItems() {
        return !!this.getStoresWithAllItems().length;
    }

    @computed
    get storesWithAllItems() {
        return this.getStoresWithAllItems();
    }

    @computed
    get storesWithAllItemsOptions() {
        const storesWithAllItems = this.getStoresWithAllItems();

        return storesWithAllItems.map((store) => {
            return {
                value: store.id,
                label: this.getOptionLabel(store)
            };
        });
    }

    @computed
    get data() {
        return {
            deliveryMethod: this.transactionRepository.getDeliveryMethod,
            unavailableItemsInfo: this.unavailableItemsInfo,
            editDeliveryOption: this.editDeliveryOption,
            isStoresWithAllItems: this.isStoresWithAllItems,
            storesWithAllItemsOptions: this.storesWithAllItemsOptions,
            selectedStoreWithAllItemsLabel: this.getOptionLabel(this.selectedStoreWithAllItems),
            selectedStore: this.selectedStore,
            closeLoading: this.isCloseLoading
        };
    }
}
